import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  PricingApi,
  Configuration,
} from '@clear-denver/tpc-openapi-typescript/build/v1/lookup'

const PLAN_KEYS = {
  enroll: 'tpc-enroll-5',
  renew: 'tpc-renew-online-5',
  militarySpouseEnroll: 'tpc-enroll-5-military-spouse',
}

type RatePlan = {
  currentPrice: number
  internalKey: string
  lastSyncTime: number
  name: string
  paygovRemittanceAmount: number
  productPlanKey: string
  productPlanName: string
  ratePlanId: string
  serviceLineItemKey: string
  trackingSuffix: string
}

type PricingConfig = {
  enroll?: RatePlan
  renew?: RatePlan
  militarySpouseEnroll?: RatePlan
}

export type ConfigContextValues = {
  fetchPricing: () => Promise<void>
  pricingConfig: PricingConfig | null
}

const initialState: ConfigContextValues = {
  fetchPricing: async () => null as never,
  pricingConfig: null,
}

export const PricingConfigContext =
  createContext<ConfigContextValues>(initialState)

export const PricingConfigProvider = ({
  children,
}: Readonly<{
  children: React.ReactNode
}>) => {
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null)

  const pricingClient = useMemo(
    () =>
      new PricingApi(
        new Configuration({
          basePath: process.env.NEXT_PUBLIC_LOOKUP_API_ENDPOINT,
        })
      ),
    []
  )

  const fetchPricing = useCallback(async () => {
    const res = await pricingClient.getPricingConfig()
    const plans = Object.values(res?.ratePlans ?? {}) as unknown as RatePlan[]

    const enroll =
      plans.find((p) => p.serviceLineItemKey === PLAN_KEYS.enroll) ?? null
    const renew =
      plans.find((p) => p.serviceLineItemKey === PLAN_KEYS.renew) ?? null
    const militarySpouseEnroll =
      plans.find(
        (p) => p.serviceLineItemKey === PLAN_KEYS.militarySpouseEnroll
      ) ?? null
    setPricingConfig({
      enroll: enroll as never,
      renew: renew as never,
      militarySpouseEnroll: militarySpouseEnroll as never,
    })
  }, [pricingClient])

  useEffect(() => {
    void fetchPricing()
  }, [fetchPricing])

  return (
    <PricingConfigContext.Provider value={{ pricingConfig, fetchPricing }}>
      {children}
    </PricingConfigContext.Provider>
  )
}

export const usePricingConfigContext = () => useContext(PricingConfigContext)
